import { inject, Service } from '@angular/core'
import { Storage } from '@ionic/storage-angular'
import { combinedCheckerHelper } from '../helpers/storage-checker.helper'
import { captureException } from '../helpers/error-capture.helper'

const TRANSIENT_INDEXEDDB_ERROR_NAMES = new Set([
  'UnknownError',
  'AbortError',
  'InvalidStateError',
  'TransactionInactiveError',
  'QuotaExceededError',
])

const isTransientStorageError = (error: unknown): boolean =>
  error === null ||
  error === undefined ||
  (typeof error === 'object' && TRANSIENT_INDEXEDDB_ERROR_NAMES.has((error as { name?: unknown }).name as string))

const describeStorageError = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`
  }
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return String((error as { name: unknown }).name)
  }
  return typeof error === 'string' && error ? error : 'rejected without error detail'
}

@Service()
export class StorageService {
  private storage = inject(Storage)

  #storage: Storage | null = null

  async init() {
    try {
      if (!combinedCheckerHelper()) {
        return
      }
      this.#storage = await this.storage.create()
    } catch (error) {
      captureException(error, 'info', 'storage-service-init')
    }
  }

  async set(key: string, value: unknown): Promise<void> {
    if (!this.#storage) {
      return
    }
    try {
      await this.#storage.set(key, JSON.stringify(value))
    } catch (error) {
      // PREGNANT-APP-EW / PREGNANT-APP-D4: IndexedDB goes away mid-operation
      // (backgrounded tab, teardown, private mode). Persistence is best-effort:
      // report a meaningful handled event and resolve undefined so callers keep
      // working instead of tripping the global Angular error handler.
      captureException(
        new Error(`Storage set failed for key "${key}": ${describeStorageError(error)}`, { cause: error }),
        isTransientStorageError(error) ? 'info' : 'warning',
        'storage-service-set',
      )
      return undefined
    }
  }

  async get(key: string): Promise<any> {
    if (!this.#storage) {
      return Promise.resolve(undefined)
    }
    try {
      const value = await this.#storage.get(key)
      return value ? JSON.parse(value) : undefined
    } catch (error) {
      captureException(
        new Error(`Storage get failed for key "${key}": ${describeStorageError(error)}`, { cause: error }),
        isTransientStorageError(error) ? 'info' : 'warning',
        'storage-service-get',
      )
      return undefined
    }
  }
}
